import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ShoppingBag, Clock, ChefHat, PackageCheck, Check, X, Phone, Award, Euro, Eye, Timer, CheckCircle2 } from "lucide-react";
import OrderEtaDialog, { fmtReadyAt } from "../components/OrderEtaDialog";
import { useAuth } from "../context/AuthContext";
import BranchMultiSelect from "../components/BranchMultiSelect";
import SavedViews from "../components/SavedViews";
import Sparkline from "../components/Sparkline";
import DateRangePresets from "../components/DateRangePresets";

const NEXT_STATUS = {
  received: { next: "in_prep", label: "Prendi in carico", icon: ChefHat, color: "bg-amber-600 hover:bg-amber-700" },
  in_prep: { next: "ready", label: "Segna pronto", icon: PackageCheck, color: "bg-emerald-600 hover:bg-emerald-700" },
  ready: { next: "delivered", label: "Consegnato", icon: Check, color: "bg-blue-600 hover:bg-blue-700" },
};

const STATUS_META = {
  received: { label: "Ricevuto", color: "pill-gold" },
  in_prep: { label: "In preparazione", color: "pill-gold" },
  ready: { label: "Pronto", color: "pill-mint" },
  delivered: { label: "Consegnato", color: "pill-muted" },
  cancelled: { label: "Annullato", color: "pill-muted" },
};

export default function CustomerOrders() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("open");
  const [branches, setBranches] = useState([]);
  const [branchIds, setBranchIds] = useState([]);
  const [customerQuery, setCustomerQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [manualPrompt, setManualPrompt] = useState(null); // { order, autoMatched, matchedItems }
  const [etaFor, setEtaFor] = useState(null); // ordine per cui scegliere i minuti di attesa
  const [etaBusy, setEtaBusy] = useState(false);
  const [manualAmount, setManualAmount] = useState("");
  const [collections, setCollections] = useState([]);
  const [deliverPrompt, setDeliverPrompt] = useState(null); // ordine in consegna: scelta raccolte punti
  const [deliverCollIds, setDeliverCollIds] = useState(null);
  const activeColls = collections.filter((c) => c.status === "active");
  const selectedCollIds = deliverCollIds ?? activeColls.filter((c) => c.pos_default).map((c) => c.id);
  const toggleColl = (id) => setDeliverCollIds(selectedCollIds.includes(id) ? selectedCollIds.filter((x) => x !== id) : [...selectedCollIds, id]);
  useEffect(() => { api.get("/collections").then((r) => setCollections(r.data || [])).catch(() => {}); }, []);
  const CollectionPicker = () => activeColls.length > 1 ? (
    <div className="flex flex-wrap items-center gap-2" data-testid="order-collections-picker">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500">Accredita su</span>
      {activeColls.map((c) => { const on = selectedCollIds.includes(c.id); return (
        <button key={c.id} type="button" onClick={() => toggleColl(c.id)} data-testid={`order-collection-toggle-${c.id}`} className={`pill cursor-pointer text-[11px] ${on ? "" : "pill-zinc opacity-60"}`} style={on ? { background: c.color, color: "#111" } : undefined}>{on ? "✓ " : ""}{c.name}</button>
      ); })}
    </div>
  ) : null;
  const [confirmingDeliver, setConfirmingDeliver] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);

  const load = async () => {
    const status_filter = filter === "open" ? null : filter;
    const params = status_filter ? { status_filter } : {};
    if (branchIds.length > 0) params.branch_id = branchIds.join(",");
    const { data } = await api.get("/customer-orders", { params });
    const filtered = filter === "open"
      ? data.filter((o) => !["delivered", "cancelled"].includes(o.status))
      : data;
    setRows(filtered);
  };

  // Client-side filter chain (customer + date range + amount range) applied on top of server-side fetch
  const visibleRows = React.useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    const fromMs = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : 0;
    const toMs = dateTo ? new Date(dateTo + "T23:59:59").getTime() : Infinity;
    const minAmt = amountMin === "" ? -Infinity : Number(amountMin);
    const maxAmt = amountMax === "" ? Infinity : Number(amountMax);
    return rows.filter((o) => {
      if (q) {
        const hay = `${o.customer_name || ""} ${o.customer_card || ""} ${o.customer_phone || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const t = new Date(o.created_at).getTime();
      if (t < fromMs || t > toMs) return false;
      const amt = Number(o.total_amount ?? o.manual_amount ?? 0);
      if (amt < minAmt || amt > maxAmt) return false;
      return true;
    });
  }, [rows, customerQuery, dateFrom, dateTo, amountMin, amountMax]);

  const resetFilters = () => { setCustomerQuery(""); setDateFrom(""); setDateTo(""); setBranchIds([]); setAmountMin(""); setAmountMax(""); };

  const currentFilters = { filter, branchIds, customerQuery, dateFrom, dateTo, amountMin, amountMax };
  const applyView = (f) => {
    if (f.filter !== undefined) setFilter(f.filter);
    if (f.branchIds !== undefined) setBranchIds(f.branchIds || []);
    if (f.customerQuery !== undefined) setCustomerQuery(f.customerQuery || "");
    if (f.dateFrom !== undefined) setDateFrom(f.dateFrom || "");
    if (f.dateTo !== undefined) setDateTo(f.dateTo || "");
    if (f.amountMin !== undefined) setAmountMin(f.amountMin ?? "");
    if (f.amountMax !== undefined) setAmountMax(f.amountMax ?? "");
  };

  const buildExportParams = () => {
    const params = {};
    if (filter && filter !== "open" && filter !== "all") params.status_filter = filter;
    if (branchIds.length > 0) params.branch_id = branchIds.join(",");
    if (customerQuery.trim()) params.customer_query = customerQuery.trim();
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (amountMin !== "") params.amount_min = Number(amountMin);
    if (amountMax !== "") params.amount_max = Number(amountMax);
    return params;
  };

  const exportFile = async (fmt) => {
    try {
      const res = await api.get(`/customer-orders/export.${fmt}`, { params: buildExportParams(), responseType: "blob" });
      const mime = fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url; a.download = `ordini.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} scaricato`);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const exportXlsx = () => exportFile("xlsx");
  const exportPdf = () => exportFile("pdf");

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, branchIds.join(",")]);
  useEffect(() => {
    if (user?.role === "admin") api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  }, [user]);

  const advance = async (o, etaMinutes = null) => {
    const nxt = NEXT_STATUS[o.status];
    if (!nxt) return;
    if (nxt.next === "in_prep" && etaFor === null) { setEtaFor(o); return; }
    if (nxt.next === "delivered" && activeColls.length > 1 && !deliverPrompt) { setDeliverPrompt(o); return; }
    if (nxt.next !== "in_prep" && nxt.next !== "delivered" && !window.confirm(`Confermi "${nxt.label}" per l'ordine di ${o.customer_name}?`)) return;
    if (nxt.next === "delivered" && activeColls.length <= 1 && !window.confirm(`Confermi "${nxt.label}" per l'ordine di ${o.customer_name}?`)) return;
    setEtaBusy(true);
    try {
      const payload = { status: nxt.next };
      if (nxt.next === "in_prep" && etaMinutes) payload.eta_minutes = etaMinutes;
      if (nxt.next === "delivered" && activeColls.length > 1) payload.collection_ids = selectedCollIds;
      const { data } = await api.patch(`/customer-orders/${o.id}`, payload);
      setDeliverPrompt(null);
      if (nxt.next === "delivered" && !(data && data.needs_manual_amount)) setDeliverCollIds(null);
      // Handle delivery flow: server may ask for a manual amount when catalog auto-match failed
      if (nxt.next === "delivered" && data && data.needs_manual_amount) {
        setManualPrompt({ order: o, autoMatched: data.auto_matched_total || 0, matchedItems: data.matched_items || [] });
        setManualAmount("");
        return;
      }
      const earnMsg = data?.earn ? ` · ${(data.earn.collections || []).length > 1 ? data.earn.collections.map((c) => `+${c.points} pt ${c.name}`).join(", ") : `+${data.earn.points} pt (€${data.earn.amount_eur.toFixed(2)})`}${data.earn.milestone_bonus ? ` +${data.earn.milestone_bonus} bonus` : ""}` : "";
      const etaMsg = data?.ready_at && nxt.next === "in_prep" ? ` · pronto per le ${fmtReadyAt(data.ready_at)} (cliente avvisato)` : "";
      toast.success(`Stato → ${STATUS_META[nxt.next].label}${earnMsg}${etaMsg}`);
      // Auto-open WhatsApp when order becomes ready
      if (nxt.next === "ready" && o.customer_phone) {
        const phone = o.customer_phone.replace(/\s/g, "").replace("+", "");
        const itemsText = (o.items || []).map(i => `${i.qty}× ${i.name}`).join(", ");
        const msg = `Ciao ${o.customer_name}! Il tuo ordine è PRONTO 🍽️${itemsText ? "\n\n" + itemsText : ""}\n\nTi aspettiamo!`;
        const link = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
        window.open(link, "_blank");
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEtaBusy(false); setEtaFor(null); }
  };

  const confirmManualDelivery = async () => {
    if (!manualPrompt) return;
    const amt = parseFloat(manualAmount.replace(",", "."));
    if (!(amt > 0)) { toast.error("Inserisci un importo valido"); return; }
    setConfirmingDeliver(true);
    try {
      const { data } = await api.patch(`/customer-orders/${manualPrompt.order.id}`, {
        status: "delivered",
        manual_amount: amt,
        collection_ids: activeColls.length > 1 ? selectedCollIds : undefined,
      });
      const earnMsg = data?.earn ? ` · ${(data.earn.collections || []).length > 1 ? data.earn.collections.map((c) => `+${c.points} pt ${c.name}`).join(", ") : `+${data.earn.points} pt`}${data.earn.milestone_bonus ? ` +${data.earn.milestone_bonus} bonus` : ""}` : "";
      toast.success(`Ordine consegnato${earnMsg}`);
      setManualPrompt(null);
      setDeliverCollIds(null);
      setManualAmount("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setConfirmingDeliver(false); }
  };

  const reschedule = async (o, etaMinutes) => {
    if (!etaMinutes) { setEtaFor(null); return; }
    setEtaBusy(true);
    try {
      const { data } = await api.patch(`/customer-orders/${o.id}`, { status: "in_prep", eta_minutes: etaMinutes });
      toast.success(`Nuovo orario ${fmtReadyAt(data.ready_at)} inviato al cliente con le scuse`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEtaBusy(false); setEtaFor(null); }
  };

  const cancel = async (o) => {
    if (!window.confirm(`Annullare l'ordine di ${o.customer_name}?`)) return;
    try {
      await api.patch(`/customer-orders/${o.id}`, { status: "cancelled" });
      toast.success("Ordine annullato");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="customer-orders-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3 sm:sticky sm:top-0 z-20 bg-[color:var(--bg-dark)] sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 sm:py-3 sm:border-b sm:border-zinc-900" data-testid="orders-header">
        <div>
          <span className="pill pill-red"><ShoppingBag className="h-3 w-3" /> ORDINI IN ARRIVO</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Pannello ordini cliente</h1>
          <p className="text-zinc-400 mt-1">Gestisci lo stato degli ordini inviati dal portale.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end flex-wrap">
          {user?.role === "admin" && branches.length > 0 && (
            <div className="w-full sm:w-56 flex flex-col gap-1" data-testid="orders-branch-filter">
              <span className="order-field-label">Sede</span>
              <BranchMultiSelect
                branches={branches}
                value={branchIds}
                onChange={setBranchIds}
                testId="orders-branch"
              />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <span className="order-field-label">Cliente</span>
          <Input
            data-testid="orders-customer-filter"
            value={customerQuery}
            onChange={(e) => setCustomerQuery(e.target.value)}
            placeholder="🔍 Cliente (nome/carta/tel)"
            className="w-full sm:w-56 bg-zinc-950 border-zinc-800 h-10 text-sm"
          />
          </div>
          <div className="flex flex-col gap-1">
            <span className="order-field-label">Periodo</span>
            <DateRangePresets
              from={dateFrom}
              to={dateTo}
              onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
              testId="orders-date-preset"
              pageKey="orders"
            />
            <div className="flex gap-1.5 items-center">
              <Input
                type="date"
                data-testid="orders-date-from"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full sm:w-36 bg-zinc-950 border-zinc-800 h-10 text-xs"
                title="Da"
              />
              <span className="text-zinc-500 text-xs">→</span>
              <Input
                type="date"
                data-testid="orders-date-to"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full sm:w-36 bg-zinc-950 border-zinc-800 h-10 text-xs"
                title="A"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="order-field-label">Importo €</span>
          <div className="flex gap-1.5 items-center">
            <Input
              type="number"
              step="0.01"
              min="0"
              data-testid="orders-amount-min"
              value={amountMin}
              onChange={(e) => setAmountMin(e.target.value)}
              className="w-full sm:w-24 bg-zinc-950 border-zinc-800 h-10 text-xs"
              title="Importo minimo €"
              placeholder="€ min"
            />
            <span className="text-zinc-500 text-xs">–</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              data-testid="orders-amount-max"
              value={amountMax}
              onChange={(e) => setAmountMax(e.target.value)}
              className="w-full sm:w-24 bg-zinc-950 border-zinc-800 h-10 text-xs"
              title="Importo massimo €"
              placeholder="€ max"
            />
          </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="order-field-label">Stato</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="orders-filter" className="bg-zinc-950 border-zinc-800 w-full sm:w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">🔥 Aperti (non consegnati)</SelectItem>
              <SelectItem value="received">Ricevuti</SelectItem>
              <SelectItem value="in_prep">In preparazione</SelectItem>
              <SelectItem value="ready">Pronti</SelectItem>
              <SelectItem value="delivered">Consegnati</SelectItem>
              <SelectItem value="cancelled">Annullati</SelectItem>
              <SelectItem value="all">Tutti</SelectItem>
            </SelectContent>
          </Select>
          </div>
          {(customerQuery || dateFrom || dateTo || branchIds.length > 0 || amountMin !== "" || amountMax !== "") && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={resetFilters}
              data-testid="orders-filters-reset"
              className="border-zinc-800 text-zinc-400 hover:text-white h-10"
              title="Azzera filtri"
            >
              ✕ Azzera
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={exportXlsx}
            data-testid="orders-export-xlsx"
            className="border-emerald-800/50 text-emerald-300 hover:bg-emerald-500/10 h-10"
            title="Esporta Excel con i filtri correnti"
          >
            📥 XLSX
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={exportPdf}
            data-testid="orders-export-pdf"
            className="border-red-800/50 text-red-300 hover:bg-red-500/10 h-10"
            title="Esporta PDF stampabile"
          >
            📄 PDF
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <SavedViews
          page="orders"
          currentFilters={currentFilters}
          onApply={applyView}
        />
        <div className="flex items-center gap-3 flex-wrap">
          <Sparkline items={visibleRows} label="ordini ultimi 7g" color="#F59E0B" />
          <div className="text-[11px] text-zinc-500" data-testid="orders-result-counter">
            <span className="text-emerald-300 font-mono font-bold">{visibleRows.length}</span>
            {visibleRows.length !== rows.length && <> / <span className="font-mono">{rows.length}</span></>}
            {" "}ordini {visibleRows.length !== rows.length ? "corrispondono ai filtri" : "trovati"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {visibleRows.length === 0 && (
          <div className="col-span-full text-center py-12 text-zinc-500">
            {rows.length === 0
              ? `Nessun ordine ${filter === "open" ? "aperto" : "in questa vista"}.`
              : "Nessun ordine corrisponde ai filtri selezionati."}
          </div>
        )}
        {visibleRows.map((o) => {
          const nxt = NEXT_STATUS[o.status];
          const meta = STATUS_META[o.status];
          const whenSecs = Math.round((Date.now() - new Date(o.created_at).getTime()) / 1000);
          const rel = whenSecs < 60 ? `${whenSecs}s fa` : whenSecs < 3600 ? `${Math.round(whenSecs/60)}m fa` : `${Math.round(whenSecs/3600)}h fa`;
          const isUrgent = o.status !== "delivered" && o.status !== "cancelled" && whenSecs > 900;
          const phone = (o.customer_phone || "").replace(/\s/g, "").replace("+", "");
          return (
            <div key={o.id} onClick={() => setDetailOrder(o)} role="button" tabIndex={0} className={`card-elev p-4 space-y-3 cursor-pointer hover:border-amber-500/50 transition-all ${isUrgent ? "glow-red" : ""}`} data-testid={`order-card-${o.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="order-field-label">Cliente</div>
                  <div className="font-heading font-bold text-base">{o.customer_name}</div>
                  <div className="text-xs text-zinc-500 font-mono"><span className="order-field-label inline mr-1">Carta · Tel.</span>{o.customer_card} · {o.customer_phone}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-1"><span className="order-field-label inline mr-1">Ricevuto</span><Clock className="h-3 w-3" />{rel}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="order-field-label">Stato</div>
                  <span className={`pill ${meta.color}`}>{meta.label}</span>
                </div>
              </div>

              {o.items && o.items.length > 0 && (
                <div className="text-sm bg-zinc-950 border border-zinc-800 rounded-lg p-3">
                  <div className="order-field-label mb-1">Prodotti</div>
                  {o.items.map((i, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span>{i.qty}× {i.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {o.source === "scopri" && <div className="text-[11px] font-semibold inline-flex items-center rounded-md px-2 py-1 bg-sky-500/15 border border-sky-500/40 text-sky-300" data-testid={`order-source-${o.id}`}>Origine: landing /scopri</div>}
              {o.payment?.status === "COMPLETED" ? (
                <div data-testid={`order-paid-${o.id}`} className="text-xs font-bold flex items-center gap-1.5 rounded-md px-2 py-1.5 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">
                  <CheckCircle2 className="h-3.5 w-3.5" /> PAGATO {o.payment.provider === "paypal" ? "PayPal" : (o.payment.provider || "").toUpperCase()}{typeof o.payment.amount === "number" ? ` · € ${o.payment.amount.toFixed(2)}` : ""} — non incassare
                </div>
              ) : o.payment_method === "paypal" ? (
                <div data-testid={`order-paypending-${o.id}`} className="text-xs font-semibold flex items-center gap-1.5 rounded-md px-2 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-300">
                  <Timer className="h-3.5 w-3.5" /> PayPal in attesa — pagamento non ancora confermato: incassa alla consegna se non arriva
                </div>
              ) : null}
              {o.note && <div className="text-xs text-zinc-400 italic bg-amber-500/5 border border-amber-500/20 p-2 rounded"><span className="order-field-label inline mr-1 not-italic">Nota</span>💬 {o.note}</div>}
              {o.pickup_date && <div className="text-xs text-amber-300"><span className="order-field-label inline mr-1">Ritiro</span>🕒 {o.pickup_date}</div>}
              {o.ready_at && ["in_prep", "ready"].includes(o.status) && (() => {
                const late = o.status === "in_prep" && Date.now() > new Date(o.ready_at).getTime();
                return (
                  <div data-testid={`order-eta-${o.id}`} className={`text-xs flex items-center gap-1 ${late ? "text-red-300 font-semibold" : "text-sky-300"}`}>
                    <Timer className="h-3.5 w-3.5" /> {late ? "In ritardo · era previsto per le" : "Pronto per le"} {fmtReadyAt(o.ready_at)}{o.eta_minutes ? ` (${o.eta_minutes} min)` : ""}
                    {o.rescheduled_count > 0 && <span className="text-[10px] text-zinc-500 font-normal" data-testid={`order-rescheduled-${o.id}`}>· aggiornato {o.rescheduled_count}× 🙏</span>}
                  </div>
                );
              })()}

              <div className="flex gap-2 pt-2 border-t border-zinc-900" onClick={(e) => e.stopPropagation()}>
                {phone && (
                  <a
                    href={`https://wa.me/${phone}?text=${encodeURIComponent(`Ciao ${o.customer_name}, il tuo ordine è ${meta.label}.`)}`}
                    target="_blank" rel="noreferrer"
                    className="p-2 rounded-md bg-zinc-950 border border-zinc-800 hover:border-emerald-500 text-emerald-300"
                    data-testid={`order-wa-${o.id}`}
                  >
                    <Phone className="h-4 w-4" />
                  </a>
                )}
                {nxt && (
                  <Button
                    data-testid={`order-advance-${o.id}`}
                    size="sm"
                    onClick={() => advance(o)}
                    className={`flex-1 h-9 text-white ${nxt.color}`}
                  >
                    <nxt.icon className="h-3.5 w-3.5 mr-1" /> {nxt.label}
                  </Button>
                )}
                {o.status === "in_prep" && o.ready_at && (
                  <Button
                    data-testid={`order-reschedule-${o.id}`}
                    variant="outline" size="sm" title="Nuovo orario di ritiro: il cliente riceve le scuse con il nuovo orario"
                    onClick={() => setEtaFor({ ...o, reschedule: true })}
                    className="border-zinc-800 hover:border-sky-500 hover:text-sky-300 h-9"
                  >
                    <Timer className="h-3.5 w-3.5" />
                  </Button>
                )}
                {o.status !== "delivered" && o.status !== "cancelled" && (
                  <Button
                    data-testid={`order-cancel-${o.id}`}
                    variant="outline" size="sm"
                    onClick={() => cancel(o)}
                    className="border-zinc-800 hover:border-red-500 hover:text-red-300 h-9"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              {o.status === "delivered" && typeof o.earn_points === "number" && o.earn_points > 0 && (
                <div className="text-[11px] rounded-md bg-emerald-500/10 border border-emerald-500/25 text-emerald-200 px-2 py-1.5 flex items-center gap-1.5" data-testid={`order-earn-${o.id}`}>
                  <Award className="h-3.5 w-3.5" />
                  <span className="font-semibold">+{o.earn_points} pt</span>
                  <span className="text-emerald-300/70">accreditati (€ {(o.earn_amount_eur || 0).toFixed(2)})</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Manual amount dialog when auto-match fails on delivery */}
      <OrderEtaDialog order={etaFor} mode={etaFor?.reschedule ? "reschedule" : "start"} busy={etaBusy} onClose={() => setEtaFor(null)}
        onConfirm={(m) => (etaFor?.reschedule ? reschedule(etaFor, m) : advance(etaFor, m))} />
      <Dialog open={!!deliverPrompt} onOpenChange={(o) => { if (!o) setDeliverPrompt(null); }}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md" data-testid="deliver-collections-dialog">
          <DialogHeader><DialogTitle>Consegna ordine — raccolte punti</DialogTitle></DialogHeader>
          {deliverPrompt && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">Ordine di <b className="text-zinc-100">{deliverPrompt.customer_name}</b>: scegli su quali raccolte accreditare i punti (come in cassa).</p>
              <CollectionPicker />
              <Button className="w-full" disabled={etaBusy || selectedCollIds.length === 0} onClick={() => advance(deliverPrompt)} data-testid="deliver-collections-confirm">{etaBusy ? "Consegno…" : "Conferma consegna"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!manualPrompt} onOpenChange={(o) => { if (!o) { setManualPrompt(null); setManualAmount(""); } }}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md" data-testid="manual-amount-dialog">
          <DialogHeader><DialogTitle>Consegna ordine — inserisci importo</DialogTitle></DialogHeader>
          {manualPrompt && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">
                Non è stato possibile stimare automaticamente l'importo dell'ordine di <b className="text-zinc-100">{manualPrompt.order.customer_name}</b>.
                Inserisci il totale € pagato per accreditare i punti fedeltà.
              </p>
              {manualPrompt.order.items?.length > 0 && (
                <div className="text-xs bg-zinc-950 border border-zinc-800 rounded-lg p-3 space-y-1">
                  <div className="order-field-label mb-1">Righe ordine</div>
                  {manualPrompt.order.items.map((it, i) => (
                    <div key={i} className="flex justify-between text-zinc-300">
                      <span>{it.qty}× {it.name}</span>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-1">
                  <Euro className="h-3.5 w-3.5 text-amber-300" /> Totale pagato (€)
                </Label>
                <Input
                  data-testid="manual-amount-input"
                  type="number"
                  step="0.01"
                  min="0"
                  autoFocus
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="Es. 12.50"
                  className="bg-zinc-950 border-zinc-800 text-lg font-mono"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setManualPrompt(null); setManualAmount(""); }}
                  className="border-zinc-800"
                  data-testid="manual-amount-cancel"
                >Annulla</Button>
                <Button
                  data-testid="manual-amount-confirm"
                  disabled={confirmingDeliver || !manualAmount}
                  onClick={confirmManualDelivery}
                  className="flex-1 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"
                >
                  <Check className="h-4 w-4 mr-2" /> {confirmingDeliver ? "Conferma…" : "Conferma consegna"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Dettaglio ordine (anche per ordini consegnati/annullati) */}
      <Dialog open={!!detailOrder} onOpenChange={(o) => !o && setDetailOrder(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md max-h-[85vh] overflow-y-auto" data-testid="order-detail-dialog">
          {detailOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-amber-300" /> Ordine #{detailOrder.id?.slice(-6).toUpperCase()}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`pill ${STATUS_META[detailOrder.status]?.color}`}>{STATUS_META[detailOrder.status]?.label}</span>
                  <span className="text-[11px] text-zinc-500">{new Date(detailOrder.created_at).toLocaleString("it-IT")}</span>
                </div>
                <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3">
                  <div className="order-field-label mb-1">Cliente</div>
                  <div className="font-heading font-bold">{detailOrder.customer_name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{detailOrder.customer_card} · {detailOrder.customer_phone}</div>
                </div>
                {detailOrder.items?.length > 0 && (
                  <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3" data-testid="order-detail-items">
                    <div className="order-field-label mb-2">Prodotti ordinati</div>
                    {detailOrder.items.map((i, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-zinc-800/50 last:border-0">
                        <span>{i.qty}× {i.name}</span>
                        {i.price != null && <span className="text-zinc-400 text-xs">€ {(i.qty * i.price).toFixed(2)}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {detailOrder.note && (
                  <div className="text-xs italic text-amber-200 bg-amber-500/5 border border-amber-500/20 p-2 rounded" data-testid="order-detail-note">💬 {detailOrder.note}</div>
                )}
                {detailOrder.pickup_date && (
                  <div className="text-xs text-amber-300">🕒 Ritiro previsto: {detailOrder.pickup_date}</div>
                )}
                {detailOrder.status === "delivered" && typeof detailOrder.earn_points === "number" && (
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3" data-testid="order-detail-earn">
                    <div className="flex items-center gap-2 text-emerald-200 font-bold">
                      <Award className="h-4 w-4" /> +{detailOrder.earn_points} punti accreditati
                    </div>
                    <div className="text-[11px] text-emerald-300/70">Totale pagato € {(detailOrder.earn_amount_eur || 0).toFixed(2)}</div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
